import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Daily entry for the device's own participant (SPECIFIKACE.md ch. 8 point 2).
 *
 * Laid out for a phone, which is how this gets used: one exercise per row, and next to the input a
 * reset, a "<" and a ">" -- all sized for a thumb, all stepping in multiples of that exercise's
 * norm so nobody has to type "90" for three sets of thirty push-ups.
 */
@Composable
fun DailyEntryScreen(token: String, participantId: String) {
    val state = rememberAsyncState()
    var participantName by remember { mutableStateOf<String?>(null) }
    var exerciseTypes by remember { mutableStateOf<List<ExerciseType>>(emptyList()) }
    var ratingTiers by remember { mutableStateOf<List<RatingTier>>(emptyList()) }
    var selectedDate by remember { mutableStateOf(todayIso()) }
    var edits by remember { mutableStateOf(EntryEdits()) }
    var streak by remember { mutableStateOf<Streak?>(null) }
    var illnesses by remember { mutableStateOf<List<Illness>>(emptyList()) }
    var illnessDate by remember { mutableStateOf(todayIso()) }
    var illnessExpanded by remember { mutableStateOf(false) }
    var savedDate by remember { mutableStateOf<String?>(null) }
    var editRevision by remember { mutableStateOf(0) }

    suspend fun loadEntries(date: String) {
        val entries = Api.fetchEntries(token, date = date, participantId = participantId)
        // Ignore a response that lost the race to a newer date selection.
        if (date == selectedDate) edits = EntryEdits.from(entries)
    }

    LaunchedEffect(Unit) {
        state.run {
            participantName = Api.fetchParticipant(participantId)?.name
            exerciseTypes = Api.fetchExerciseTypes()
            ratingTiers = Api.fetchRatingTiers()
            streak = Api.fetchStreaks(token, participantId).firstOrNull()
            illnesses = Api.fetchIllnesses(token, participantId)
            loadEntries(selectedDate)
        }
    }

    val pointsToday = edits.pointsOf(participantId, exerciseTypes)
    val tier = ratingTiers.tierFor(pointsToday)
    val sickOnSelectedDate = illnesses.any { it.includes(selectedDate) }
    val activeIllness = illnesses.firstOrNull { it.endedOn == null }
    // The live preview is only meaningful for today. On a backfilled day the streak on screen is
    // still the one running now, and folding that day's points into it would be nonsense.
    val editingToday = selectedDate == todayIso()
    val currentStreak = streak
    val preview = currentStreak?.let {
        if (editingToday && !sickOnSelectedDate) it.previewWith(pointsToday) else it.asPreview()
    }

    fun changeDate(date: String) {
        selectedDate = date
        savedDate = null
        state.launch { loadEntries(date) }
    }

    fun changeEdits(updated: EntryEdits) {
        edits = updated
        editRevision += 1
        savedDate = null
    }

    fun save() {
        val date = selectedDate
        val revision = editRevision
        val changes = edits.changes(exerciseTypes)
        state.launch {
            changes.save(token, date, actingParticipantId = participantId)
            if (selectedDate == date) edits = edits.committed(changes)
            streak = Api.fetchStreaks(token, participantId).firstOrNull()
            // Inputs stay editable while the request is running. Do not mark a newer local edit
            // as saved merely because the older request has just completed.
            if (editRevision == revision && selectedDate == date) savedDate = date
        }
    }

    fun reportIllness() {
        val startedOn = illnessDate
        state.launch {
            Api.reportIllness(token, participantId, startedOn)
            illnesses = Api.fetchIllnesses(token, participantId)
            streak = Api.fetchStreaks(token, participantId).firstOrNull()
            illnessDate = todayIso()
            illnessExpanded = false
        }
    }

    fun endIllness() {
        val illness = activeIllness ?: return
        val endedOn = illnessDate
        state.launch {
            Api.endIllness(token, illness.id, endedOn)
            illnesses = Api.fetchIllnesses(token, participantId)
            streak = Api.fetchStreaks(token, participantId).firstOrNull()
            illnessDate = todayIso()
            illnessExpanded = false
        }
    }

    fun updateIllness(illness: Illness, startedOn: String, endedOn: String?) {
        state.launch {
            Api.updateIllness(token, illness.id, startedOn, endedOn)
            illnesses = Api.fetchIllnesses(token, participantId)
            streak = Api.fetchStreaks(token, participantId).firstOrNull()
        }
    }

    fun deleteIllness(illness: Illness) {
        if (!window.confirm("Opravdu smazat tento záznam nemoci? Hodnocení a streak se přepočítají.")) return
        state.launch {
            Api.deleteIllness(token, illness.id)
            illnesses = Api.fetchIllnesses(token, participantId)
            streak = Api.fetchStreaks(token, participantId).firstOrNull()
            illnessDate = todayIso()
        }
    }

    Div(attrs = { classes("entry-form") }) {
        P(attrs = { classes("entry-who") }) { Text("Zapisuješ za: ${participantName ?: "…"}") }

        if (state.loaded) {
            Div(attrs = { classes("entry-preview") }) {
                Span(attrs = { classes("entry-points") }) { Text("Body dnes: ${formatNumber(pointsToday)}") }
                if (sickOnSelectedDate) TierBadge("Nemocný") else tier?.let { TierBadge(it.label) }
                preview?.let { StreakBadges(it, includeInactive = true) }
            }
            Div(attrs = { classes("entry-status-row") }) {
                if (currentStreak != null && preview != null) {
                    FreezeStatus(currentStreak, preview, pointsToday, editingToday)
                }
                Button(attrs = {
                    classes("illness-toggle")
                    if (activeIllness != null) classes("illness-toggle-active")
                    attr("aria-expanded", illnessExpanded.toString())
                    attr("title", if (activeIllness == null) "Nahlásit nemoc" else "Ukončit nemoc")
                    onClick {
                        val opening = !illnessExpanded
                        if (opening) {
                            illnessDate = activeIllness?.let {
                                maxOf(it.startedOn, shiftDaysIso(todayIso(), -1))
                            } ?: todayIso()
                        }
                        illnessExpanded = opening
                    }
                }) {
                    Text(if (activeIllness == null) "Nemoc" else "Nemoc aktivní")
                }
            }
        }

        if (illnessExpanded) {
            IllnessControl(
                activeIllness = activeIllness,
                illnesses = illnesses,
                date = illnessDate,
                busy = state.busy,
                onChangeDate = { illnessDate = it },
                onReport = ::reportIllness,
                onEnd = ::endIllness,
                onUpdate = ::updateIllness,
                onDelete = ::deleteIllness,
            )
        }
        DateSelector(selectedDate = selectedDate, onChangeDate = ::changeDate)
        ErrorMessage(state.error)

        if (!state.loaded) {
            LoadingText()
        } else {
            Div(attrs = { classes("entry-rows") }) {
                exerciseTypes.forEach { type ->
                    ExerciseRow(
                        type = type,
                        text = edits.textOf(EntryKey(participantId, type.id)),
                        onChange = { changeEdits(edits.withText(EntryKey(participantId, type.id), it)) },
                        onStep = { step ->
                            val key = EntryKey(participantId, type.id)
                            changeEdits(edits.withValue(key, step(edits.valueOf(key), type.pointsDivisor)))
                        },
                    )
                }
            }
            SaveRow("Uložit", state.busy, savedDate, selectedDate, ::save)
        }
    }
}

@Composable
private fun IllnessControl(
    activeIllness: Illness?,
    illnesses: List<Illness>,
    date: String,
    busy: Boolean,
    onChangeDate: (String) -> Unit,
    onReport: () -> Unit,
    onEnd: () -> Unit,
    onUpdate: (Illness, String, String?) -> Unit,
    onDelete: (Illness) -> Unit,
) {
    val ending = activeIllness != null
    val validEnd = activeIllness == null || date >= activeIllness.startedOn
    Div(attrs = { classes("illness-panel") }) {
        P(attrs = { classes("illness-status") }) {
            Text(
                if (activeIllness == null) {
                    "Nemoc není nahlášena."
                } else {
                    "Nemocný od ${formatCzechDate(activeIllness.startedOn)}."
                },
            )
        }
        Div(attrs = { classes("illness-controls") }) {
            Span { Text(if (ending) "Poslední den nemoci" else "První den nemoci") }
            Input(type = InputType.Date, attrs = {
                value(date)
                onInput { if (it.value.isNotBlank()) onChangeDate(it.value) }
            })
            PrimaryButton(
                label = when {
                    busy -> "Ukládám…"
                    ending -> "Ukončit nemoc"
                    else -> "Nahlásit nemoc"
                },
                enabled = !busy && validEnd,
                onClick = if (ending) onEnd else onReport,
            )
        }
        if (!validEnd) {
            P(attrs = { classes("error-text") }) {
                Text("Konec nemoci nemůže být před ${formatCzechDate(activeIllness!!.startedOn)}.")
            }
        }
        if (illnesses.isNotEmpty()) {
            P(attrs = { classes("illness-records-title") }) { Text("Nahlášená období") }
            Div(attrs = { classes("illness-records") }) {
                illnesses.forEach { illness ->
                    IllnessRecord(
                        illness = illness,
                        busy = busy,
                        onUpdate = onUpdate,
                        onDelete = onDelete,
                    )
                }
            }
        }
    }
}

@Composable
private fun IllnessRecord(
    illness: Illness,
    busy: Boolean,
    onUpdate: (Illness, String, String?) -> Unit,
    onDelete: (Illness) -> Unit,
) {
    var startedOn by remember(illness.id, illness.startedOn) { mutableStateOf(illness.startedOn) }
    var endedOn by remember(illness.id, illness.endedOn) { mutableStateOf(illness.endedOn.orEmpty()) }
    val valid = startedOn.isNotBlank() && (endedOn.isBlank() || endedOn >= startedOn)
    val changed = startedOn != illness.startedOn || endedOn.ifBlank { null } != illness.endedOn

    Div(attrs = { classes("illness-record") }) {
        Div(attrs = { classes("illness-record-row") }) {
            Div(attrs = { classes("illness-record-dates") }) {
                Span { Text("Od") }
                Input(type = InputType.Date, attrs = {
                    value(startedOn)
                    onInput { startedOn = it.value }
                })
                Span { Text("Do") }
                Input(type = InputType.Date, attrs = {
                    value(endedOn)
                    onInput { endedOn = it.value }
                })
                if (endedOn.isBlank()) Span(attrs = { classes("field-note") }) { Text("probíhá") }
            }
            Div(attrs = { classes("illness-record-actions") }) {
                Button(attrs = {
                    classes("illness-record-save")
                    if (busy || !valid || !changed) disabled()
                    onClick { onUpdate(illness, startedOn, endedOn.ifBlank { null }) }
                }) { Text("Uložit") }
                Button(attrs = {
                    classes("illness-delete")
                    if (busy) disabled()
                    onClick { onDelete(illness) }
                }) { Text("Smazat") }
            }
        }
        if (!valid) P(attrs = { classes("error-text") }) { Text("Konec nemoci nesmí být před začátkem.") }
    }
}

/**
 * What the freeze is doing, in one line.
 *
 * The pending case is the one that matters: it is the only place in the app where the streak hangs
 * on something the user still has to do TODAY, so it names the target and the gap to it rather
 * than leaving them to work out what a snowflake means.
 */
@Composable
private fun FreezeStatus(streak: Streak, preview: StreakPreview, pointsToday: Double, editingToday: Boolean) {
    if (!streak.freezeEnabled) return
    when {
        preview.pending -> {
            val held = preview.live.firstOrNull()?.streak ?: 0
            val target = streak.pointsToRepay()
            val needed = buildString {
                append("❄ Řada ${czechDays(held)} visí na vlásku – dnes potřebuješ ")
                append(streak.repayMinLabel ?: "vyšší úroveň")
                target?.let { append(" (${formatNumber(it)} b.)") }
                if (editingToday) append(", máš ${formatNumber(pointsToday)}") 
                append(".")
            }
            P(attrs = { classes("freeze-line", "freeze-warning") }) { Text(needed) }
        }

        preview.repaid ->
            P(attrs = { classes("freeze-line", "freeze-ok") }) { Text("✓ Zmrazení doplaceno, řada pokračuje.") }

        streak.freezeBalance > 0 ->
            P(attrs = { classes("freeze-line") }) { Text("❄ Zmrazení připraveno – jeden vynechaný den řadu nezabije.") }

        else -> P(attrs = { classes("freeze-line") }) {
            Text("🔒 ${streak.freezeProgress}/${streak.freezeEarnDays} dní k dalšímu zmrazení.")
        }
    }
}

/** One exercise: its label, then reset / `<` / input / `>` grouped together on the right. */
@Composable
private fun ExerciseRow(
    type: ExerciseType,
    text: String,
    onChange: (String) -> Unit,
    onStep: ((Double, Double) -> Double) -> Unit,
) {
    Div(attrs = { classes("entry-row") }) {
        Span(attrs = { classes("field-label") }) { Text("${type.name} (${type.unitLabel})") }
        Div(attrs = { classes("entry-controls") }) {
            StepButton("⟲", "Vynulovat ${type.name}", "reset-btn") { onStep { _, _ -> 0.0 } }
            StepButton("<", "Ubrat ${type.name}", "step-btn") { onStep(::stepDown) }
            TextField(text, onChange, placeholder = "0", decimal = true, cssClass = "entry-input")
            StepButton(">", "Přidat ${type.name}", "step-btn") { onStep(::stepUp) }
        }
    }
}

@Composable
private fun StepButton(label: String, ariaLabel: String, cssClass: String, onClick: () -> Unit) {
    Button(attrs = {
        classes(cssClass)
        attr("aria-label", ariaLabel)
        onClick { onClick() }
    }) { Text(label) }
}
